import {
	Button,
	ButtonGroup,
	Card,
	Heading,
	IconButton,
	LuIcon,
	Menu,
	Separator,
	Text,
	toast,
} from "@overmap-ai/blocks"
import {
	type Asset,
	type AssetTypeStatus,
	type Issue,
	type IssueType,
	pluralize,
	type Stored,
	toFileNameSafeString,
	EMPTY_ARRAY,
	ERROR_MSG_UNEXPECTED,
} from "@overmap-ai/common"
import {
	APIError,
	selectAssetTypeById,
	selectAssetTypeIdentifiersOfAssetType,
	selectAssetTypeStatusesOfAssetType,
	selectIssuesOfProject,
	selectIssueTypesOfOrganization,
	selectLatestAssetTypeFieldsOfAssetType,
	selectLatestProcedureTypeFieldsOfProcedureType,
	selectProcedureInitiativeById,
	selectProcedureStepsOfProcedureType,
	selectProcedureTypeById,
	selectUsersOfProject,
} from "@overmap-ai/core"
import { useOpenState } from "@overmap-ai/hooks"
import { getRouteApi, Link, useNavigate } from "@tanstack/react-router"
import type { InitialTableState, SortingState, Updater, VisibilityState } from "@tanstack/react-table"
import { cx } from "class-variance-authority"
import { saveAs } from "file-saver"
import Papa from "papaparse"
import { memo, useCallback, useMemo } from "react"

import { IssueCommandMenu } from "../../components/CommandMenus/IssueCommandMenu"
import { ViewContainer } from "../../components/Containers/ViewContainer"
import { ProcedurePdfExportOptionsDialog } from "../../components/Dialogs/ProcedurePdfExportOptionsDialog"
import { ProcedureBulkDropdownMenu } from "../../components/DropdownMenus/ProcedureBulkDropdownMenu"
import { AssetMultiSelect } from "../../components/DropdownMultiSelects/AssetMultiSelectDropdown"
import { ProcedureInitiativeDataFilterMenu } from "../../components/FilterMenus/ProcedureInitiativeDataFilterMenu"
import { useDeserializeFieldSchema } from "../../components/Form/hooks"
import { ProcedureStepStackedBarChart } from "../../components/Graphs/ProcedureStepStackedBarChart"
import { AssetTypeIcon } from "../../components/Icons/AssetTypeIcon"
import { ProcedureTypeIcon } from "../../components/Icons/ProcedureTypeIcon"
import { computeProcedureInitiativeIndex } from "../../components/IndexLabels/utils"
import { useProcedureInitiativeDataTable, useProcedureInitiativeDataTableColumns } from "../../components/Tables"
import { ColumnVisibilityDropdown, PaginationControl } from "../../components/Tables/components"
import { defaultRowsPerPageOptions } from "../../components/Tables/constants"
import { tableIconMapping } from "../../components/Tables/mappings"
import { Table } from "../../components/Tables/Table"
import { getProcedureInitiativeDataCsvExporter } from "../../csv/CSVExporter/procedures"
import { interactiveTextCva } from "../../cva"
import { FilterRenderer } from "../../filtering/components"
import type { AnyFilter } from "../../filtering/filter"
import { useFilterManager, useFilters } from "../../filtering/hooks"
import { getProcedureInitiativeDataFilterSchema } from "../../filtering/schemas/ProcedureInitiativeDataFilterSchema"
import { useIssuesWithIdentifiers } from "../../hooks/issues"
import {
	useProcedureInitiativeData,
	useProcedureIntiativeCompletionData,
	useFieldSchemasAndConditionsOfProcedureSteps,
} from "../../hooks/procedures"
import { useConfirmDeleteDialog } from "../../hooks/utils"
import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import {
	setProcedureInitiativeDataColumnSorting,
	setProcedureInitiativeDataColumnVisibility,
	setProcedureInitiativeDataFilters,
	useAppDispatch,
	useAppSelector,
	useShallowEqaulSelector,
} from "../../store"
import {
	selectAssetsOfProjectAndAssetType,
	selectColumnSortingOfProcedureInitiativeData,
	selectColumnVisibilityOfProcedureInitiativeData,
	selectProcedureInitiativeDataFiltersOfProcedureInitiative,
} from "../../store/selectors"
import type { ProcedureInitiativeData } from "../../typings"
import type { ProcedurePdfExportOptions } from "../../typings/reports"
import { formatTimestampDate } from "../../utils/date"

const rowsPerPageOptions = [25, ...defaultRowsPerPageOptions]

const initialTableState: InitialTableState = {
	sorting: [
		{
			id: "submitted_at",
			desc: true,
		},
	],
	pagination: {
		pageSize: rowsPerPageOptions[0],
	},
}

const routeApi = getRouteApi(
	"/_loggedIn/$organizationId/_map/_layout/$projectId/_projectMap/initiatives/$procedureInitiativeId",
)

export const ProcedureInitiativeDetailView = memo(() => {
	const { organizationId, projectId, procedureInitiativeId } = routeApi.useParams()

	const procedureInitiative = useAppSelector(selectProcedureInitiativeById(procedureInitiativeId))
	const procedureType = useAppSelector(selectProcedureTypeById(procedureInitiative?.procedure_type ?? ""))
	const assetType = useAppSelector(selectAssetTypeById(procedureInitiative?.asset_type ?? ""))

	if (!procedureInitiative || !procedureType || !assetType) {
		throw new Error(`procedureInitiative, assetType or procedureType not found in store.`)
	}

	const dispatch = useAppDispatch()
	const navigate = useNavigate()
	const openConfirmDeleteDialog = useConfirmDeleteDialog()
	const assetTypeIdentifiers = useShallowEqaulSelector(selectAssetTypeIdentifiersOfAssetType(assetType.uuid))
	const assetTypeStatuses = useShallowEqaulSelector(selectAssetTypeStatusesOfAssetType(assetType.uuid))
	const assetTypeFields = useAppSelector(selectLatestAssetTypeFieldsOfAssetType(assetType.uuid))
	const procedureSteps = useShallowEqaulSelector(selectProcedureStepsOfProcedureType(procedureType.uuid))
	const procedureTypeFields = useAppSelector(selectLatestProcedureTypeFieldsOfProcedureType(procedureType.uuid))
	const serializedFilters = useAppSelector(
		selectProcedureInitiativeDataFiltersOfProcedureInitiative(procedureInitiative.uuid),
	)
	const columnSorting = useAppSelector(selectColumnSortingOfProcedureInitiativeData(procedureInitiative.uuid))
	const columnVisibility = useAppSelector(selectColumnVisibilityOfProcedureInitiativeData(procedureInitiative.uuid))
	const [showIssueCommandMenu, openIssueCommandMenu, closeIssueCommandMenu] = useOpenState(false)
	const assets = useShallowEqaulSelector(selectAssetsOfProjectAndAssetType(projectId, assetType.uuid))
	const users = useShallowEqaulSelector(selectUsersOfProject(projectId))
	const issueTypes = useShallowEqaulSelector(selectIssueTypesOfOrganization(organizationId))
	const issues = useShallowEqaulSelector(selectIssuesOfProject(projectId))
	const issuesAndIdentifiers = useIssuesWithIdentifiers(issues)
	const procedureData = useProcedureInitiativeData(procedureInitiative.uuid)

	const addableAssets = useMemo(() => {
		const procedureAssetIds = new Set(procedureData.map(({ procedure }) => procedure.asset))
		return assets.filter(({ uuid }) => !procedureAssetIds.has(uuid))
	}, [procedureData, assets])

	const procedureTypeFieldSchema = useDeserializeFieldSchema(procedureTypeFields?.fields ?? EMPTY_ARRAY)
	const assetTypeFieldSchema = useDeserializeFieldSchema(assetTypeFields?.fields ?? EMPTY_ARRAY)
	const procedureStepFieldSchemasAndConditions = useFieldSchemasAndConditionsOfProcedureSteps(procedureSteps)
	const procedureStepCompletionData = useProcedureIntiativeCompletionData(procedureInitiative)

	const procedureDataFilterSchema = useMemo(() => {
		return getProcedureInitiativeDataFilterSchema({
			procedureTypeFieldSchema,
			procedureSteps,
			procedureStepFieldSchemasAndConditions,
			users,
			assetTypeIdentifiers,
			assetTypeFieldSchema,
			assetTypeStatuses,
		})
	}, [
		assetTypeFieldSchema,
		assetTypeIdentifiers,
		assetTypeStatuses,
		procedureStepFieldSchemasAndConditions,
		procedureSteps,
		procedureTypeFieldSchema,
		users,
	])

	const filters = useMemo(() => {
		return procedureDataFilterSchema.deserializeFilters(serializedFilters)
	}, [procedureDataFilterSchema, serializedFilters])

	const handleFiltersChange = useCallback(
		(filters: AnyFilter[]) => {
			dispatch(
				setProcedureInitiativeDataFilters({
					id: procedureInitiative.uuid,
					filters: procedureDataFilterSchema.serializeFilters(filters),
				}),
			)
		},
		[dispatch, procedureDataFilterSchema, procedureInitiative.uuid],
	)

	const handleColumnVisibilityChange = useCallback(
		(updater: Updater<VisibilityState>) => {
			dispatch(
				setProcedureInitiativeDataColumnVisibility({
					id: procedureInitiative.uuid,
					columnVisibility: typeof updater === "function" ? updater(columnVisibility) : updater,
				}),
			)
		},
		[dispatch, procedureInitiative.uuid, columnVisibility],
	)

	const handleSortingChange = useCallback(
		(updater: Updater<SortingState>) => {
			dispatch(
				setProcedureInitiativeDataColumnSorting({
					id: procedureInitiative.uuid,
					columnSorting: typeof updater === "function" ? updater(columnSorting) : updater,
				}),
			)
		},
		[dispatch, procedureInitiative.uuid, columnSorting],
	)

	const procedureDataFilterManager = useFilterManager({
		filterSchema: procedureDataFilterSchema,
		filters,
		onFilterChange: handleFiltersChange,
	})

	const filteredProcedureData = useFilters({
		filterManager: procedureDataFilterManager,
		filters,
		data: procedureData,
	})

	const columns = useProcedureInitiativeDataTableColumns({
		procedureType,
		procedureTypeFieldSchema,
		procedureSteps,
		procedureStepFieldSchemasAndConditions,
		assetTypeIdentifiers,
		assetTypeFieldSchema,
		assetTypeStatuses,
	})

	const table = useProcedureInitiativeDataTable({
		columns,
		data: filteredProcedureData,
		initialState: initialTableState,
		state: {
			columnVisibility,
			sorting: columnSorting,
		},
		onColumnVisibilityChange: handleColumnVisibilityChange,
		onSortingChange: handleSortingChange,
	})

	const handleCreateProceduresForAssets = useCallback(
		(assets: Stored<Asset>[]) => {
			void sdk.procedures.bulkAdd(
				assets.map((asset) => ({
					procedure_type: procedureType.uuid,
					procedure_initiative: procedureInitiative.uuid,
					asset: asset.uuid,
					project: projectId,
					issue: null,
				})),
			)
		},
		[procedureType.uuid, procedureInitiative.uuid, projectId],
	)

	const handleRowClick = useCallback(
		({ procedure }: ProcedureInitiativeData) => {
			void navigate({
				from: "/$organizationId/$projectId/initiatives/$procedureInitiativeId",
				to: "/$organizationId/$projectId/procedures/$procedureId",
				params: {
					procedureId: procedure.uuid,
				},
			})
		},
		[navigate],
	)

	const handleDelete = useCallback(() => {
		openConfirmDeleteDialog({
			title: "Delete initiave",
			description: "Are you sure you want to delete this initiave? This action cannot be undone.",
			onAction: async () => {
				await navigate({
					from: "/$organizationId/$projectId/initiatives/$procedureInitiativeId",
					to: "/$organizationId/$projectId/initiatives",
				})

				void sdk.procedureInitiatives.delete(procedureInitiative.uuid)
			},
		})
	}, [procedureInitiative.uuid, navigate, openConfirmDeleteDialog])

	const getSelectedRows = useCallback(() => table.getSelectedRowModel().rows, [table])

	const handleExportToCsv = useCallback(() => {
		const csvExporter = getProcedureInitiativeDataCsvExporter(
			getSelectedRows().map((row) => row.original),
			{
				procedureSteps: procedureSteps,
				procedureTypeFieldSchema: procedureTypeFieldSchema,
				procedureStepFieldSchemas: procedureStepFieldSchemasAndConditions,
				assetTypeIdentifiers,
				assetTypeFieldSchema,
				assetTypeStatuses,
			},
		)
		const csvData = Papa.unparse(csvExporter.toPapaUnparseFormat(), { quotes: true })
		const blob = new Blob([csvData], { type: "text/csv" })
		saveAs(blob, toFileNameSafeString(procedureType.name, ".csv"))
	}, [
		getSelectedRows,
		procedureType.name,
		procedureSteps,
		procedureTypeFieldSchema,
		procedureStepFieldSchemasAndConditions,
		assetTypeIdentifiers,
		assetTypeFieldSchema,
		assetTypeStatuses,
	])

	const [showPdfExportOptionsDialog, openPdfExportOptionsDialog, closePdfExportOptionsDialog] = useOpenState(false)

	const handleExportToPdf = useCallback(
		(options: ProcedurePdfExportOptions) => {
			const selectedRows = table.getSelectedRowModel().rows.map((row) => row.original.procedure.uuid)

			void sdk.procedures
				.getProceduresPdfReport(procedureType.uuid, selectedRows, options)
				.then(() => {
					toast.success({
						title: "Report requested",
						description: `Requested report for ${selectedRows.length} ${pluralize("procedure", selectedRows.length)}. Visit the Reports page when it is ready.`,
					})
				})
				.catch((e) => {
					toast.error({
						title: "Failed to generate report",
						description: e instanceof APIError ? e.message : ERROR_MSG_UNEXPECTED,
					})
				})
		},
		[table, procedureType.uuid],
	)

	const handleBulkStatusChange = useCallback(
		(assetTypeStatus: Stored<AssetTypeStatus>) => {
			const assetIds = getSelectedRows().map((row) => row.original.procedure.asset)
			sdk.assets.bulkUpdate(assetIds.map((uuid) => ({ uuid, status: assetTypeStatus.uuid })))
		},
		[getSelectedRows],
	)

	const handleLinkIssues = useCallback(
		(issue: Stored<Issue>) => {
			const assetIds = getSelectedRows().map((row) => row.original.procedure.asset)

			const [_, promise] = sdk.issueAssociations.bulkAdd(
				assetIds.map((assetId) => ({
					asset: assetId,
					issue: issue.uuid,
				})),
			)

			void promise.then(() => {
				toast.success({
					title: "Success",
					description: "Successfully linked issue to asset.",
				})
			})
		},
		[getSelectedRows],
	)

	const handleCreateIssueAndLink = useCallback(
		(issueType: Stored<IssueType>) => {
			const [offlineIssue, _promise] = sdk.issues.add({
				title: "",
				description: null,
				assigned_to: null,
				due_date: null,
				issue_type: issueType.uuid,
				priority: issueType.default_priority!,
				status: issueType.default_status!,
				project: projectId,
			})

			handleLinkIssues(offlineIssue)

			void navigate({
				from: "/$organizationId/$projectId/initiatives/$procedureInitiativeId",
				to: "/$organizationId/$projectId/issues/$issueId",
				params: (prev) => ({ ...prev, issueId: offlineIssue.uuid }),
			})
		},
		[projectId, handleLinkIssues, navigate],
	)

	const handleBulkDelete = useCallback(() => {
		const procedureIds = getSelectedRows().map((row) => row.original.procedure.uuid)

		openConfirmDeleteDialog({
			title: "Delete procedures",
			description: `The ${procedureIds.length} ${pluralize("procedure", procedureIds.length)} will be deleted. This action cannot be undone.`,
			onAction: () => void sdk.procedures.bulkDelete(procedureIds),
		})

		table.resetRowSelection()
	}, [table, getSelectedRows, openConfirmDeleteDialog])

	const hasProcedureSteps = procedureSteps.length > 0

	return (
		<ViewContainer>
			<section className="flex size-full flex-col gap-4">
				<div className="flex flex-wrap items-center justify-between gap-2">
					<div className="flex items-center gap-2">
						<Heading size="xl" className="flex shrink-0 items-center gap-2">
							<ProcedureTypeIcon procedureTypeId={procedureType.uuid} />
							{computeProcedureInitiativeIndex(procedureType, procedureInitiative)}
						</Heading>
					</div>

					<div className="flex items-center gap-2">
						<AssetMultiSelect
							assets={addableAssets}
							onValuesChange={handleCreateProceduresForAssets}
							size="sm"
						>
							<Button size="sm" variant="surface">
								<LuIcon icon={modelIconMapping.add} />
								Add
							</Button>
						</AssetMultiSelect>

						<Menu.Root>
							<Menu.ClickTrigger>
								<IconButton variant="ghost" size="sm" accentColor="base">
									<LuIcon icon={modelIconMapping.more} />
								</IconButton>
							</Menu.ClickTrigger>
							<Menu.Content size="sm">
								<Menu.Item onSelect={handleDelete}>
									<LuIcon icon={modelIconMapping.delete} />
									Delete
								</Menu.Item>
							</Menu.Content>
						</Menu.Root>
					</div>
				</div>

				<Separator size="full" />

				<div className="flex size-full flex-col gap-4 overflow-y-auto overflow-x-hidden">
					<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
						<Card className="flex grow flex-col gap-4" size="sm">
							<div className="flex flex-col gap-1">
								<Text accentColor="base" size="sm">
									Procedure type
								</Text>
								<Text accentColor="base" size="sm">
									<ProcedureTypeIcon procedureTypeId={procedureType.uuid} className="align-sub" />
									&nbsp;
									<Link
										className={cx("min-w-0", interactiveTextCva())}
										from="/$organizationId/$projectId/initiatives/$procedureInitiativeId"
										to="/$organizationId/$projectId/procedure-types/$procedureTypeId"
										params={(prev) => ({ ...prev, procedureTypeId: procedureType.uuid })}
									>
										<Text className="truncate" highContrast>
											{procedureType.name}
										</Text>
									</Link>
								</Text>
							</div>

							<div className="flex flex-col gap-1">
								<Text accentColor="base" size="sm">
									Asset type
								</Text>
								<Text accentColor="base" size="sm">
									<AssetTypeIcon assetTypeId={assetType.uuid} className="align-sub" />
									&nbsp;
									<Link
										className={cx("min-w-0", interactiveTextCva())}
										from="/$organizationId/$projectId/initiatives/$procedureInitiativeId"
										to="/$organizationId/$projectId/asset-types/$assetTypeId"
										params={(prev) => ({ ...prev, assetTypeId: assetType.uuid })}
									>
										<Text className="truncate" highContrast>
											{assetType.name}
										</Text>
									</Link>
								</Text>
							</div>

							<div className="flex flex-col gap-1">
								<Text accentColor="base" size="sm">
									Created
								</Text>
								<Text size="sm">{formatTimestampDate(procedureInitiative.submitted_at)}</Text>
							</div>
						</Card>

						{hasProcedureSteps ? (
							<Card className="h-64! flex flex-col gap-2 overflow-hidden" size="sm">
								<Text>Step progress</Text>

								<Text size="xs" accentColor="base">
									Completion status of each step across all procedures.
								</Text>

								<ProcedureStepStackedBarChart
									procedureSteps={procedureSteps}
									height="100%"
									width="100%"
									procedureStepCompletionDatas={procedureStepCompletionData}
								/>
							</Card>
						) : null}
					</div>

					<Card className="flex flex-col gap-2" size="sm">
						<ButtonGroup
							className="flex items-center justify-between"
							size="xs"
							variant="soft"
							accentColor="base"
						>
							<div className="flex items-center gap-2">
								<Text>Procedures</Text>
								<ProcedureInitiativeDataFilterMenu
									filterManager={procedureDataFilterManager}
									procedureTypeFieldSchema={procedureTypeFieldSchema}
									procedureSteps={procedureSteps}
									procedureStepFieldSchemasAndConditions={procedureStepFieldSchemasAndConditions}
									users={users}
									assetTypeIdentifiers={assetTypeIdentifiers}
									assetTypeFieldSchema={assetTypeFieldSchema}
									assetTypeStatuses={assetTypeStatuses}
									align="start"
									size="sm"
								>
									<Button>
										<LuIcon icon={modelIconMapping.filter} />
										Filter
									</Button>
								</ProcedureInitiativeDataFilterMenu>

								{procedureDataFilterManager.getFilters().length > 0 && (
									<IconButton onClick={procedureDataFilterManager.removeAll}>
										<LuIcon icon={modelIconMapping.close} />
									</IconButton>
								)}
							</div>

							<div className="flex gap-2">
								{getSelectedRows().length > 0 ? (
									<ProcedureBulkDropdownMenu
										onExportCsv={handleExportToCsv}
										onExportPdf={openPdfExportOptionsDialog}
										onLinkIssue={openIssueCommandMenu}
										onDelete={handleBulkDelete}
										statuses={assetTypeStatuses}
										onStatusSelect={handleBulkStatusChange}
										size="sm"
									>
										<Button>
											<LuIcon icon={modelIconMapping.command} />
											Actions
										</Button>
									</ProcedureBulkDropdownMenu>
								) : null}

								<ColumnVisibilityDropdown table={{ ...table }} align="end" size="sm">
									<IconButton>
										<LuIcon icon={tableIconMapping.columns} />
									</IconButton>
								</ColumnVisibilityDropdown>
							</div>
						</ButtonGroup>

						<FilterRenderer filterManager={procedureDataFilterManager} size="sm" />

						<div className="w-full overflow-x-auto">
							<Table
								className="w-full"
								size="sm"
								variant="surface"
								table={{ ...table }}
								onRowClick={handleRowClick}
								compact
								interactive
								emptyText="No procedures"
							/>
						</div>

						<PaginationControl table={{ ...table }} rowPerPageOptions={rowsPerPageOptions} />
					</Card>
				</div>
			</section>

			<IssueCommandMenu
				issuesAndIdentifiers={issuesAndIdentifiers}
				issueTypes={issueTypes}
				onIssueSelect={handleLinkIssues}
				onCreateIssue={handleCreateIssueAndLink}
				open={showIssueCommandMenu}
				onOpenChange={closeIssueCommandMenu}
			/>
			<ProcedurePdfExportOptionsDialog
				key={procedureType.uuid}
				procedureTypeId={procedureType.uuid}
				onSubmitOptions={handleExportToPdf}
				open={showPdfExportOptionsDialog}
				onOpenChange={closePdfExportOptionsDialog}
			/>
		</ViewContainer>
	)
})
ProcedureInitiativeDetailView.displayName = "ProcedureInitiativeDetailView"
